import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Col, Row } from 'antd-v5';
import { RootState } from 'app/AppStore';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { useFetchCategoryImages } from 'domains/catalog/Catalog.hooks';
import {
  getDHCatalogViewMode,
  getExplodedTree,
  getLastSearchedVehicleKey,
  setDHCatalogViewMode,
} from 'domains/catalog/Catalog.store';
import { CarPartsViewMode } from 'domains/catalog/Catalog.types';
import { Box, Flex, Text } from 'UI';
import { head } from 'utils';
import { useBreakpointSelectorFull } from 'utils/hooks/useBreakpoint';
import { OFFSET_HEADER, OFFSET_PADDING, OFFSET_SUB_HEADER, useOffsetTop } from 'utils/hooks/useOffsetTop';
import CarPartGroupItem from './CarPartGroupItem';
import { RoutineVehicleMaintenanceGroupCard } from './CarPartGroupItem/RoutineVehicleMaintenanceGroupCard';
import { CarPartGroupsListView } from './CarPartGroupsListView/CarPartGroupsListView';
import { ScrollWrapper } from './CarPartGroupsSection.styled';
import { isMaintenanceNodeId, ROUTINE_VEHICLE_MAINTENANCE_GROUP_NODE_ID } from './CarPartGroupsSection.utils';
import ViewToggle from './ViewToggle/ViewToggle';
import { SViewHeader } from './ViewToggle/ViewToggle.styled';

export const CarPartElementId = 'car-part-group-';

interface CarPartGroupsSectionProps {
  setCatalogShowMenu?: (b: boolean) => void;
}

const CarPartGroupsSection = ({ setCatalogShowMenu }: CarPartGroupsSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const breakpointSelectorFull = useBreakpointSelectorFull();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const viewMode = useSelector(getDHCatalogViewMode);
  const { carPartGroupId: enlargedViewNodeId } = useParams<{
    carPartGroupId?: string;
  }>();
  const vehicleExplodedViewsRawData = useSelector((state: RootState) => getExplodedTree(state, vehicleKey))?.data;
  const vehicleExplodedViewsFiltered = useMemo(
    () => vehicleExplodedViewsRawData?.filter((item) => item.nodeId !== ROUTINE_VEHICLE_MAINTENANCE_GROUP_NODE_ID),
    [vehicleExplodedViewsRawData],
  );
  const [vehicleExplodedViews, setVehicleExplodedViews] = useState(vehicleExplodedViewsFiltered);

  const handleViewModeChange = (mode: CarPartsViewMode) => {
    dispatch(setDHCatalogViewMode(mode));
    setCatalogShowMenu?.(mode !== 'LIST');
  };
  const offsetTop = useOffsetTop('RENAULT_CATALOG') + OFFSET_HEADER + OFFSET_SUB_HEADER + OFFSET_PADDING;
  const rvmNode = vehicleExplodedViewsRawData?.find((item) => isMaintenanceNodeId(item.nodeId));

  useFetchCategoryImages();

  useEffect(() => {
    if (vehicleExplodedViewsFiltered) {
      setVehicleExplodedViews(vehicleExplodedViewsFiltered);
    }
    // eslint-disable-next-line
  }, [vehicleKey, vehicleExplodedViewsFiltered]);

  const setEnlargedNode = useCallback(() => {
    const order = head(vehicleExplodedViewsFiltered?.filter((item) => item?.nodeId === enlargedViewNodeId))?.index;
    if (!order) return;
    // normalize order to count from zero
    const index = order - 2;
    if (index % 2 === 0) {
      setVehicleExplodedViews(vehicleExplodedViewsFiltered);
    } else {
      const x = index;
      const y = index - 1;
      const list = vehicleExplodedViewsFiltered;
      if (list) {
        const vehicleExplodedPermuted = Object.assign([], list, { [x]: list[y], [y]: list[x] });
        setVehicleExplodedViews(vehicleExplodedPermuted);
      }
    }
  }, [enlargedViewNodeId, setVehicleExplodedViews, vehicleExplodedViewsFiltered]);

  useEffect(() => {
    if (viewMode !== 'VISUAL') return;
    setEnlargedNode();
    setTimeout(() => {
      document
        .getElementById(CarPartElementId + enlargedViewNodeId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enlargedViewNodeId, viewMode]);

  return (
    <>
      <Flex direction={'column'} justify={'center'} align={'center'}>
        <SViewHeader justify={'space-between'} align={'center'}>
          <Text type="heading3" color="white" transform={'capitalize'} disableGutter>
            {t('catalog.parts.category.car_parts', 'Car parts')}
          </Text>
          <ViewToggle viewMode={viewMode} onChange={handleViewModeChange} />
        </SViewHeader>
      </Flex>

      <DataContainer
        data={vehicleExplodedViewsRawData}
        ErrorState={() => (
          <ErrorWithLabel
            label={t('catalog.parts.backend_error', 'Car parts are temporarily unavailable, please try again later.')}
          />
        )}
      >
        {viewMode === 'LIST' ? (
          <CarPartGroupsListView rvmNode={rvmNode} lvl1Nodes={vehicleExplodedViewsFiltered ?? []} />
        ) : (
          <Row gutter={[30, 30]}>
            {rvmNode && (
              <Col span={24}>
                <RoutineVehicleMaintenanceGroupCard rvmNode={rvmNode} lvl1Nodes={vehicleExplodedViewsFiltered ?? []} />
              </Col>
            )}
            {vehicleExplodedViews?.map((item) => {
              if (!item?.nodeId || !item?.index || isMaintenanceNodeId(item?.nodeId)) return null;
              return (
                <Col
                  span={
                    item?.nodeId === enlargedViewNodeId
                      ? 24
                      : breakpointSelectorFull({
                          xs: 24,
                          sm: 24,
                          md: 24,
                          lg: 24,
                          xl: 12,
                          xml: 12,
                          xxl: 12,
                        })
                  }
                  key={`${CarPartElementId}${item?.nodeId}`}
                >
                  <ScrollWrapper offsetTop={offsetTop} id={`${CarPartElementId}${item?.nodeId}`}>
                    <CarPartGroupItem
                      key={item.nodeId}
                      boxNodeId={item.nodeId}
                      label={item.label}
                      svgImageKey={item?.imageKey}
                      enlarged={enlargedViewNodeId === item.nodeId}
                      details={item.items}
                    />
                  </ScrollWrapper>
                </Col>
              );
            })}
          </Row>
        )}
      </DataContainer>
      <Box height={15} />
    </>
  );
};

export default CarPartGroupsSection;
